import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, notificationsApi, discipleshipApi } from '../api/client';
import {
  Home, Users, Building2, HeartHandshake, GraduationCap, User,
  Flame, Zap, Sparkles, ChevronRight, ShieldCheck,
  Bell, BookOpen, Moon, Sun, HandHeart, Scroll, MessageCircle, Settings, ChevronDown,
  GitBranch, Globe, LogOut,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import PushPrompt from './PushPrompt';
import './Layout.css';
// Lives in its own leaf module, not here: Layout -> RecipientsContext ->
// PrayerScreen -> useNavDots would otherwise close an import cycle back into
// this file. Re-exported so existing `from './Layout'` imports keep working.
import { NavDotContext, useNavDots } from '../context/NavDotContext';
export { NavDotContext, useNavDots };

import { FaithCheckinProvider } from '../screens/FaithCheckin';
import { EvangelismCheckinProvider } from '../screens/evangelism/EvangelismCheckin';
import { RecipientsProvider } from '../context/RecipientsContext';
import WhatMovedNotice from './WhatMovedNotice';

const LOGO = '/logo192.png';

function getInitials(n = '') { return n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'; }

/**
 * Does this bottom-nav item own the current URL?
 *
 * A section is more than one path now — Home covers the feed, stories and the
 * journal; People covers the lineage drill-downs that stay at the top level.
 * NavLink can only match its own `to`, so sections declare `matchPaths` and we
 * resolve the active item ourselves.
 */
function sectionIsActive(item, pathname) {
  const paths = item.matchPaths || [item.path];
  return paths.some(p =>
    p === '/' ? pathname === '/' : (pathname === p || pathname.startsWith(p + '/'))
  );
}

export default function Layout({ children }) {
  const { isAdmin, masquerade, stopMasquerade, signOut } = useAuth();
  const location    = useLocation();
  const { t }       = useTranslation();
  const [showMore, setShowMore]         = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [profileData, setProfileData]     = useState(null);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [prayerDot, setPrayerDot]         = useState(false);
  const [wallDot, setWallDot]             = useState(false);
  const [disciplePending, setDisciplePending] = useState(0);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('ignite-dark');
    if (saved === '1') return true;
    if (saved === '0') return false;
    // First visit — follow the device OS dark mode setting
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });

  // Apply / remove dark-mode class on body
  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    document.documentElement.style.backgroundColor = darkMode ? '#111312' : '';
    localStorage.setItem('ignite-dark', darkMode ? '1' : '0');
  }, [darkMode]);

  // Scroll the main scroll container to the top on every route change so users
  // don't land mid-page after navigating from one screen to another. The
  // .main-content element is the scroll viewport on desktop; the window/body
  // scrolls on mobile. Older Android WebViews silently ignore the
  // `{behavior:'instant'}` option form, so we use the legacy two-arg call and
  // also set scrollTop directly. We re-run on the next frame to defeat the
  // WebView's own scroll-restoration after the route change paints.
  useEffect(() => {
    const reset = () => {
      try {
        const mc = mainContentRef.current;
        if (mc) { mc.scrollTop = 0; mc.scrollLeft = 0; }
        window.scrollTo(0, 0);
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body)            document.body.scrollTop            = 0;
      } catch {}
    };
    reset();
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reset);
  }, [location.pathname]);

  // Follow OS dark/light changes in real time (e.g. auto mode, sunset trigger)
  // Only auto-follows if the user hasn't manually diverged from the system setting
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    function handleSystemChange(e) {
      const saved = localStorage.getItem('ignite-dark');
      const systemIsDark = e.matches;
      const appMatchesSystem = (systemIsDark && saved === '1') || (!systemIsDark && saved === '0');
      if (appMatchesSystem) setDarkMode(systemIsDark);
    }
    mq.addEventListener('change', handleSystemChange);
    return () => mq.removeEventListener('change', handleSystemChange);
  }, []);
  const navigate = useNavigate();
  const refreshDotsRef = useRef(() => {});
  const mainContentRef = useRef(null);

  const handleBrandClick = useCallback((e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      mainContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  // Tapping the section you are already in takes you back to its top level and
  // default tab. This is the universal "I'm lost" escape hatch, so it has to
  // work from anywhere inside the section — including sibling paths like
  // /journal under Home, which is why it takes the whole item, not just a path.
  const handleNavClick = useCallback((e, item) => {
    if (sectionIsActive(item, location.pathname)) {
      e.preventDefault();
      if (location.pathname !== item.path) navigate(item.path);
    }
    mainContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, navigate]);

  // Build nav items with translations — inside component so t() is available
  const NAV_ITEMS_T = [
    // Primary group
    { path: '/',              Icon: Home,           label: t('nav.feed'),          desc: t('nav.feedDesc'),         group: 'primary' },
    { path: '/stories',       Icon: Globe,          label: t('nav.stories'),        desc: t('nav.storiesDesc'),     group: 'primary' },
    { path: '/prayer',        Icon: HeartHandshake, label: t('nav.prayer'),         desc: t('nav.prayerDesc'),      group: 'primary' },
    { path: '/people',        Icon: Users,          label: t('nav.people'),         desc: t('nav.peopleDesc'),      group: 'primary' },
    { path: '/communities',   Icon: Building2,      label: t('nav.communities'),    desc: t('nav.communitiesDesc'), group: 'primary' },
    { path: '/grow',          Icon: GraduationCap,  label: t('nav.courses'),        desc: t('nav.coursesDesc'),     group: 'primary' },
    { path: '/journal',       Icon: Scroll,         label: t('nav.journal'),         desc: t('nav.journalDesc'),    group: 'primary' },
    // Secondary group
    { path: '/pray-now',      Icon: HandHeart,      label: t('nav.prayNow'),        desc: t('nav.prayNowDesc'),    group: 'secondary' },
    { path: '/grow/bible-study', Icon: BookOpen,    label: t('nav.bibleStudy'),     desc: t('nav.bibleStudyDesc'), group: 'secondary' },
    { path: '/people/lineage', Icon: GitBranch,     label: t('nav.myLineage'),       desc: t('nav.myLineageDesc'),   group: 'secondary' },
    // Hidden items
    { path: '/settings',      Icon: Settings,       label: t('nav.settings'),        desc: t('nav.settingsDesc'),     desktopHide: true },
    { path: '/messages',      Icon: MessageCircle,  label: t('nav.messages'),        desc: t('nav.messagesDesc'),     mobileHide: true, desktopHide: true },
    { path: '/notifications', Icon: Bell,           label: t('nav.notifications'),  desc: t('nav.notifDesc'),        mobileHide: true, desktopHide: true },
    { path: '/profile',       Icon: User,           label: t('nav.profile'),        desc: t('nav.profileDesc'),      mobileHide: true, desktopHide: true },
  ];
  const ADMIN_ITEM_T = { path: '/admin', Icon: ShieldCheck, label: t('nav.admin'), desc: t('nav.adminDesc') };

  const QUICK_ACTIONS_T = [
    { Icon: Flame,          label: t('nav.quickActions.shareReflection'), path: '/'            },
    { Icon: HeartHandshake, label: t('nav.quickActions.addPrayer'),        path: '/prayer'      },
    { Icon: Users,          label: t('nav.quickActions.findCommunity'),    path: '/communities' },
    { Icon: GraduationCap,  label: t('nav.quickActions.startCourse'),      path: '/grow'     },
  ];

  // The five mobile destinations. `path` is where the tab goes and where
  // re-tapping it returns you; `matchPaths` is everything the tab owns.
  // Order is fixed and never personalised — muscle memory is the point.
  const BOTTOM_MAIN_T = [
    { path: '/',            Icon: Home,           label: t('nav.home'),
      matchPaths: ['/', '/stories', '/journal'] },
    { path: '/prayer',      Icon: HeartHandshake, label: t('nav.prayer'),
      // Pray Now is an action launched from the Prayer calendar, so it keeps
      // Prayer lit rather than orphaning the user with nothing selected.
      matchPaths: ['/prayer', '/pray-now'] },
    { path: '/people',      Icon: Users,          label: t('nav.people'),
      // /lineage/* still resolves for already-sent notification links, and the
      // pending / relationship drill-downs live at the top level by design.
      matchPaths: ['/people', '/lineage'] },
    { path: '/communities', Icon: Building2,      label: t('nav.communities'),
      matchPaths: ['/communities', '/groups', '/well'] },
    { path: '/grow',        Icon: GraduationCap,  label: t('nav.growShort'),
      matchPaths: ['/grow', '/bible-study'] },
  ];

  // Load profile data for avatar in top header
  useEffect(() => {
    api.get('/api/profile').then(d => setProfileData(d.user)).catch(() => {});

    // Prayer calendar dot — today's events not yet prayed for
    const fetchPrayerDot = () =>
      api.get('/api/prayer/today-unprayed').then(d => setPrayerDot(d.count > 0)).catch(() => {});
    fetchPrayerDot();

    // Prayer wall dot — unseen discover items (includes system items)
    const fetchWallDot = () =>
      api.get('/api/prayer-wall/discover-count').then(d => setWallDot(d.count > 0)).catch(() => {});
    fetchWallDot();

    // Expose refresh function via ref so NavDotContext stays stable
    refreshDotsRef.current = () => { fetchPrayerDot(); fetchWallDot(); };

    // Refresh dots every 5 minutes
    const dotTimer = setInterval(() => { fetchPrayerDot(); fetchWallDot(); }, 5 * 60 * 1000);

    // Fetch unread notification count and poll every 60s. Piggyback the
    // discipleship pending count on the same interval — same cost shape.
    const fetchUnread = () => notificationsApi.list().then(d => setUnreadCount(d.unreadCount || 0)).catch(() => {});
    const fetchDisciplePending = () =>
      discipleshipApi.pending()
        .then(d => setDisciplePending(Array.isArray(d?.requests) ? d.requests.length : 0))
        .catch(() => {});
    fetchUnread();
    fetchDisciplePending();
    const timer = setInterval(() => { fetchUnread(); fetchDisciplePending(); }, 60000);

    function handleNotifRead(e) {
      if (e.detail?.delta === 'zero') setUnreadCount(0);
      else if (typeof e.detail?.delta === 'number') setUnreadCount(n => Math.max(0, n + e.detail.delta));
    }
    window.addEventListener('ignite:notif-read', handleNotifRead);
    return () => { clearInterval(timer); clearInterval(dotTimer); window.removeEventListener('ignite:notif-read', handleNotifRead); };
  }, []);

  // Show push prompt after 30s — user is engaged, not first-second jarring
  useEffect(() => {
    const t = setTimeout(() => setShowPushPrompt(true), 30000);
    return () => clearTimeout(t);
  }, []);

  // Close profile menu on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    }
    if (showProfileMenu) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showProfileMenu]);

  const showRightSidebar = location.pathname === '/';
  const isNotifActive = location.pathname === '/notifications';

  // Avatar display
  const avatarContent = profileData?.avatar
    ? <img src={profileData.avatar} alt="profile" style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover'}}/>
    : getInitials(profileData?.name || '');

  return (
    <FaithCheckinProvider>
      <EvangelismCheckinProvider>
      <RecipientsProvider>
      <div className="layout">

      {/* ── Masquerade banner ── */}
      {masquerade && (
        <div className="masquerade-banner">
          <span className="masquerade-banner-text">
            Viewing as <strong>{masquerade.targetName}</strong>
          </span>
          <button className="masquerade-banner-exit" onClick={() => { stopMasquerade(); }}>
            Exit Masquerade
          </button>
        </div>
      )}

      {/* ── Mobile top header ── */}
      <header className="top-header" style={masquerade ? { top: 'var(--masq-banner-h, 36px)' } : undefined}>
        <div className="top-header-inner">
          <button className="header-brand" onClick={handleBrandClick} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={LOGO} alt="Ignite" className="header-logo" />
            <span className="header-title">Ignite</span>
          </button>
          <div className="header-right">
            <button className="header-notif-btn" onClick={() => setDarkMode(d => !d)} title={darkMode ? t('common.lightMode') : t('common.darkMode')}>
              {darkMode ? <Sun size={19} strokeWidth={1.75}/> : <Moon size={19} strokeWidth={1.75}/>}
            </button>
            <NavLink to="/messages" className={({ isActive }) => `header-notif-btn ${isActive ? 'active' : ''}`}>
              <MessageCircle size={20} strokeWidth={1.75} />
            </NavLink>
            <NavLink to="/notifications" className={`header-notif-btn ${isNotifActive ? 'active' : ''}`}>
              <Bell size={20} strokeWidth={1.75} />
              {unreadCount > 0 && (
                <span className="header-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </NavLink>
            <div style={{position:'relative'}} ref={profileMenuRef}>
              <button className="header-avatar header-avatar-btn"
                onClick={() => setShowProfileMenu(v => !v)}
                aria-label={t('nav.profile')}>
                {avatarContent}
                <ChevronDown size={11} className="header-avatar-chevron"
                  style={{position:'absolute',bottom:-2,right:-2,background:'var(--card-bg)',borderRadius:'50%',padding:1,
                    transform: showProfileMenu ? 'rotate(180deg)' : 'none', transition:'transform 0.2s'}}/>
              </button>
              {/* Account only. Pray Now, Bible Study and My Lineage used to
                  hide here, where nobody thought to look for them — they are
                  all in the bottom nav now. Nothing functional lives here. */}
              {showProfileMenu && (
                <div className="header-profile-menu" role="menu">
                  {[
                    { path:'/profile',  Icon: User,     label: t('nav.profile')  },
                    { path:'/settings', Icon: Settings, label: t('nav.settings') },
                    ...(isAdmin ? [{ path:'/admin', Icon: ShieldCheck, label: t('nav.admin') }] : []),
                  ].map(({ path, Icon, label }) => (
                    <NavLink key={path} to={path} role="menuitem"
                      className={({ isActive }) => `hpm-item${isActive ? ' active' : ''}`}
                      onClick={() => setShowProfileMenu(false)}>
                      <Icon size={15} strokeWidth={1.75}/>
                      <span>{label}</span>
                    </NavLink>
                  ))}
                  <button type="button" role="menuitem" className="hpm-item hpm-item-signout"
                    onClick={async () => {
                      setShowProfileMenu(false);
                      try { await signOut(); navigate('/auth'); } catch {}
                    }}>
                    <LogOut size={15} strokeWidth={1.75}/>
                    <span>{t('nav.signOut')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Desktop left sidebar ── */}
      <aside className="sidebar" style={masquerade ? { top: 'var(--masq-banner-h, 36px)', height: 'calc(100vh - var(--masq-banner-h, 36px))' } : undefined}>
        <div className="sidebar-brand">
          <button className="sidebar-brand-home" onClick={handleBrandClick} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={LOGO} alt="Ignite" className="sidebar-logo" />
            <span className="sidebar-title">Ignite</span>
          </button>
          <div className="sidebar-brand-icons">
            <NavLink to="/messages" className={({ isActive }) => `sidebar-icon-btn${isActive ? ' active' : ''}`} title="Messages">
              <MessageCircle size={18} strokeWidth={1.75}/>
            </NavLink>
            <NavLink to="/notifications" className={({ isActive }) => `sidebar-icon-btn${isActive ? ' active' : ''}`} title="Notifications" style={{position:'relative'}}>
              <Bell size={18} strokeWidth={1.75}/>
              {unreadCount > 0 && (
                <span className="sidebar-icon-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </NavLink>
            <NavLink to="/profile" className={({ isActive }) => `sidebar-icon-btn sidebar-avatar-btn${isActive ? ' active' : ''}`} title="Profile">
              {avatarContent}
            </NavLink>
          </div>
        </div>
        <nav className="sidebar-nav">
          {NAV_ITEMS_T.filter(i => !i.desktopHide && i.group === 'primary').map(({ path, Icon, label, desc }) => (
            <NavLink key={path} to={path} end={path === '/'}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, { path })}>
              {({ isActive }) => (
                <>
                  <div className="sidebar-item-icon">
                    <Icon size={17} strokeWidth={isActive ? 2.5 : 1.75} />
                  </div>
                  <div className="sidebar-item-text">
                    <span className="sidebar-item-label">{label}</span>
                    <span className="sidebar-item-desc">{desc}</span>
                  </div>
                  {path === '/prayer' && (prayerDot || wallDot) && (
                    <span className="sidebar-prayer-dot"/>
                  )}
                </>
              )}
            </NavLink>
          ))}
          <div className="sidebar-nav-divider"/>
          {NAV_ITEMS_T.filter(i => !i.desktopHide && i.group === 'secondary').map(({ path, Icon, label, desc }) => (
            <NavLink key={path} to={path} end={path === '/'}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              onClick={(e) => handleNavClick(e, { path })}>
              {({ isActive }) => (
                <>
                  <div className="sidebar-item-icon">
                    <Icon size={17} strokeWidth={isActive ? 2.5 : 1.75} />
                  </div>
                  <div className="sidebar-item-text">
                    <span className="sidebar-item-label">{label}</span>
                    <span className="sidebar-item-desc">{desc}</span>
                  </div>
                  {path === '/people/lineage' && disciplePending > 0 && (
                    <span className="sidebar-item-badge" aria-label={`${disciplePending} pending discipleship request${disciplePending === 1 ? '' : 's'}`}>
                      {disciplePending > 9 ? '9+' : disciplePending}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer-group">
          {isAdmin && (
            <>
              <NavLink to="/admin" className={({ isActive }) => `sidebar-footer-item${isActive ? ' active' : ''}`}>
                <ShieldCheck size={15} strokeWidth={2}/>
                <span>{t('nav.admin')}</span>
              </NavLink>
              <div className="sidebar-footer-divider"/>
            </>
          )}
          <NavLink to="/settings" className={({ isActive }) => `sidebar-footer-item${isActive ? ' active' : ''}`}>
            <Settings size={15} strokeWidth={2}/>
            <span>{t('nav.settings')}</span>
          </NavLink>
          <div className="sidebar-footer-divider"/>
          <button className="sidebar-footer-item sidebar-footer-toggle" onClick={() => setDarkMode(d => !d)} title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            {darkMode ? <Sun size={15} strokeWidth={2}/> : <Moon size={15} strokeWidth={2}/>}
            <span>{darkMode ? t('common.lightMode') : t('common.darkMode')}</span>
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main ref={mainContentRef} className={`main-content${showRightSidebar ? '' : ' main-content--wide'}`}
        style={masquerade ? { height: 'calc(100vh - var(--masq-banner-h, 36px))', marginTop: 'var(--masq-banner-h, 36px)' } : undefined}>
        <div className="content-inner">{children}</div>
      </main>

      {/* ── Desktop right sidebar ── */}
      {showRightSidebar && <div className="right-sidebar">
        <div className="widget">
          <div className="widget-header" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Flame size={14} strokeWidth={2} /> Daily Verse
          </div>
          <div className="widget-body">
            <p className="widget-verse">"I can do all things through Christ who strengthens me."</p>
            <span className="widget-verse-ref">— Philippians 4:13</span>
          </div>
        </div>
        <div className="widget">
          <div className="widget-header" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} strokeWidth={2} /> Quick Actions
          </div>
          <div className="widget-body" style={{ padding: '8px 0' }}>
            {QUICK_ACTIONS_T.map(({ Icon, label, path }) => (
              <NavLink key={path + label} to={path} className="widget-quick-btn">
                <div className="widget-quick-icon"><Icon size={14} strokeWidth={2} /></div>
                <span style={{ flex: 1 }}>{label}</span>
                <ChevronRight size={13} strokeWidth={2} style={{ opacity: 0.35 }} />
              </NavLink>
            ))}
          </div>
        </div>
        <div className="widget">
          <div className="widget-header" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={14} strokeWidth={2} /> Community
          </div>
          <div className="widget-body">
            <p style={{ fontSize: 13, color: 'var(--text-lt)', lineHeight: 1.6 }}>
              You're part of something bigger. Keep sharing, keep praying, keep growing.
            </p>
          </div>
        </div>
      </div>}

      {/* ── Mobile bottom nav (5 labelled destinations, never icon-only) ── */}
      {/* Active state is computed from each item's matchPaths rather than
          NavLink's own matching, because Home and People each span several
          paths. Plain Link + explicit aria-current, so the two never disagree. */}
      <nav className="bottom-nav" aria-label={t('nav.mainNav')}>
        {BOTTOM_MAIN_T.map((item) => {
          const { path, Icon, label } = item;
          const isActive = sectionIsActive(item, location.pathname);
          return (
            <Link key={path} to={path}
              className={`bottom-nav-item ${isActive ? 'active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={(e) => handleNavClick(e, item)}>
              <span className="bottom-nav-icon" style={{position:'relative'}}>
                <Icon size={22} strokeWidth={isActive ? 2.5 : 1.75} />
                {path === '/prayer' && (prayerDot || wallDot) && (
                  <span className="bottom-prayer-dot"/>
                )}
                {path === '/people' && disciplePending > 0 && (
                  <span className="bottom-notif-badge">{disciplePending > 9 ? '9+' : disciplePending}</span>
                )}
              </span>
              <span className="bottom-nav-label">{label}</span>
            </Link>
          );
        })}
      </nav>

      <WhatMovedNotice />
      <PushPrompt triggerShow={showPushPrompt} onDismiss={() => setShowPushPrompt(false)} />
    </div>
      </RecipientsProvider>
      </EvangelismCheckinProvider>
    </FaithCheckinProvider>
  );
}

// Re-export so App.js can import from one place
export { FaithCheckinProvider };
